import React from 'react';

import { FormInput, Trash } from 'lucide-react';

import { IdeaFieldTypes, MaxFieldOptionCharLength, MaxFieldOptions } from 'common/constants/ideas';
import { FieldTypeDataMap } from 'common/subdomain/admin/v2/constants';
import Button from 'common/uiV3/Button';
import Field from 'common/uiV3/Field';
import Input from 'common/uiV3/Input';
import { Select } from 'common/uiV3/Select';

import type {
  CustomFieldFormState,
  FieldFormState,
} from 'common/subdomain/admin/v2/hooks/useIdeaFieldForm';

type BaseFieldInputsProps = {
  formState: FieldFormState;
  setValue: (field: keyof CustomFieldFormState, value: string | string[]) => void;
  types?: IdeaFieldTypes[];
  readOnly: boolean;
  loading: boolean;
};

const TypeOptions = Object.keys(FieldTypeDataMap).map((type) => {
  const { label, icon: Icon } = FieldTypeDataMap[type as IdeaFieldTypes];

  return {
    value: type,
    label: (
      <Select.OptionLabel>
        {<Icon />} {label}
      </Select.OptionLabel>
    ),
  };
});

export default function BaseFieldInputs({
  types = Object.values(IdeaFieldTypes),
  formState,
  setValue,
  readOnly,
  loading,
}: BaseFieldInputsProps) {
  const selectedTypeOption = TypeOptions.find((option) => option.value === formState.type);

  const showOptionsInput =
    formState.type === IdeaFieldTypes.dropdown || formState.type === IdeaFieldTypes.multiselect;

  const getAutoFocusIndex = () => {
    if (
      formState.type === IdeaFieldTypes.dropdown ||
      formState.type === IdeaFieldTypes.multiselect
    ) {
      return (formState.options?.length ?? 0) - 1;
    }

    return null;
  };

  const handleOptionChange = (index: number, value: string) => {
    if (!('options' in formState)) {
      return;
    }
    const existingOptions = formState.options ?? [];
    existingOptions[index] = value;
    setValue('options', existingOptions);
  };

  const handleDeleteOption = (index: number) => () => {
    if (!('options' in formState)) {
      return;
    }
    const existingOptions = formState.options ?? [];
    setValue(
      'options',
      existingOptions.filter((_, idx) => idx !== index),
    );
  };

  const handleAddOption = () => {
    if (!('options' in formState)) {
      return;
    }
    const existingOptions = formState.options ?? [];
    setValue('options', [...existingOptions, '']);
  };

  return (
    <>
      <Field>
        <Field.Label required>Field Name</Field.Label>
        <Field.Content>
          <Input
            autoFocus
            id="fieldName"
            startIcon={FormInput}
            onChange={(value) => setValue('name', value)}
            placeholder="Field name"
            value={formState.name}
          />
        </Field.Content>
      </Field>

      <Field>
        <Field.Label required>Type</Field.Label>
        <Field.Content>
          <Select
            disabled={readOnly}
            onValueChange={(value) => setValue('type', value ?? '')}
            options={TypeOptions.filter((option) => types.includes(option.value as IdeaFieldTypes))}
            placeholder="Field type"
            value={selectedTypeOption?.value}
          />
        </Field.Content>
      </Field>

      {showOptionsInput && (
        <Field>
          <Field.Label>Options</Field.Label>
          <Field.Content className="items-start">
            <ul className="space-y-2 w-full">
              {formState.options?.map((option, index) => (
                <li key={index} className="flex items-center gap-2">
                  <span className="w-5 flex text-base items-center justify-center">
                    {index + 1}
                  </span>
                  <Input
                    autoFocus={index === getAutoFocusIndex()}
                    onChange={(value) => handleOptionChange(index, value)}
                    maxLength={MaxFieldOptionCharLength}
                    placeholder="Option name"
                    value={option}
                  />
                  {(formState.options?.length ?? 0) > 1 && (
                    <Button
                      aria-label="Remove option"
                      color="error"
                      onClick={handleDeleteOption(index)}
                      size="small"
                      variant="plain"
                      iconOnly>
                      <Trash />
                    </Button>
                  )}
                </li>
              ))}
            </ul>
            <Button
              variant="plain"
              disabled={(formState.options?.length ?? 0) >= MaxFieldOptions || loading}
              onClick={handleAddOption}
              size="small">
              Create new option
            </Button>
          </Field.Content>
        </Field>
      )}
    </>
  );
}
